import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bars3Icon, PlusIcon, MagnifyingGlassIcon, UserCircleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useUser } from '../../contexts/UserContext';
import { useBrand } from '../../contexts/BrandContext';
import { BrandName } from '../Common/BrandedText';
import { getTierConfig } from '../../utils/planUtils';
import { isAdmin } from '../../utils/roleUtils';
import { hasModuleAccess, MODULES } from '../../utils/simplePermissions';
import NotificationCenter from '../NotificationCenter';

const Topbar = ({ onHamburger, onOpenCommandPalette }) => {
  const { user, logout } = useUser();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [openNew, setOpenNew] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const newMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const brandMonogram = brand.code === 'smbmate' ? 'SM' : 'TM';
  const isMacPlatform = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
  const commandShortcutModifier = isMacPlatform ? '⌘' : 'Ctrl';
  // The plan/trial badge is COMPANY BILLING STATE, shown only to the people who can act
  // on it. It was rendered for every seat, so an invited technician saw "Free Trial · 30
  // days left" — a $5 Crew seat has no billing relationship with the business, cannot
  // reach the billing page the badge links to, and showing them the countdown leaks the
  // company's commercial position to staff (operator saw this 2026-08-29).
  //
  // Owner and admin only. Deliberately NOT manager: a manager is a Full seat but an
  // operational one, and EDIT_BILLING is granted to OWNER alone in roleUtils — owner+admin
  // is the smallest set that keeps the account administrator informed without widening
  // that permission.
  // The "New" menu offers New Quote and New Customer, both of which land on Full-tier
  // pages. A Crew seat could open the menu and click either, then hit the route guard —
  // an action that is visible but always fails is worse than one that is absent, because
  // it reads as the app being broken rather than the feature not being theirs. Operator
  // saw exactly this as an invited technician on 2026-08-30.
  //
  // Gated per ITEM, not on the button as a whole: a role with customers but not quotes
  // (sales-adjacent office roles) should still get the half it can use.
  const canCreateQuote = hasModuleAccess(user, MODULES.QUOTES);
  const canCreateCustomer = hasModuleAccess(user, MODULES.CUSTOMERS);
  const canCreateAnything = canCreateQuote || canCreateCustomer;

  const canSeeBilling = isAdmin(user);
  const planName = canSeeBilling && user?.tier ? getTierConfig(user.tier).name : null;
  const trialDays = canSeeBilling && user?.billing?.state === 'trial' && Number.isFinite(user?.billing?.daysRemaining)
    ? user.billing.daysRemaining
    : null;
  const planLabel = !canSeeBilling
    ? null
    : trialDays === null
      ? planName
      : `${planName} · ${trialDays} day${trialDays === 1 ? '' : 's'} left`;

  // Close the "New" and profile menus on outside-click or Escape so they don't
  // stay open (or stack behind other popovers) when the user clicks elsewhere.
  useEffect(() => {
    if (!openNew && !userMenuOpen) return undefined;
    const handlePointerDown = (e) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target)) setOpenNew(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpenNew(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openNew, userMenuOpen]);

  // Render only one of mobile/desktop based on viewport. Previously both were
  // CSS-hidden (lg:hidden + hidden lg:flex) but BOTH stayed mounted in the React
  // tree — which mounted NotificationCenter twice and doubled its polling/realtime.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1024px)').matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Open command palette when clicking the search bar
  const handleSearchClick = () => {
    if (onOpenCommandPalette) {
      onOpenCommandPalette();
    }
  };

  const goNew = (type) => {
    setOpenNew(false);
    if (type === 'quote') navigate('/quotes?new=quote');
    if (type === 'customer') navigate('/customers?new=customer');
    // Jobs are created automatically from accepted quotes - no manual creation
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  return (
    <>
      {!isDesktop && (
      /* Mobile Header - Fixed positioning handled by Layout */
      <div className="app-topbar app-topbar--mobile px-4 safe-area-top">
        <div className="h-14 flex items-center justify-between gap-3">
          {/* Left: Hamburger menu */}
          <button
            className="app-topbar__icon-button p-2.5 rounded-lg flex-shrink-0"
            onClick={onHamburger}
            aria-label="Open menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="app-brand-mark w-7 h-7 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs">{brandMonogram}</span>
            </div>
            <BrandName className="text-base font-bold text-gray-900 dark:text-white hidden sm:block" />
          </div>

          {/* Center: Search button (compact on mobile) */}
          <button
            type="button"
            onClick={handleSearchClick}
            className="app-topbar__search flex-1 max-w-xs flex items-center justify-center gap-2 py-2 px-3 border rounded-lg text-sm"
          >
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            <span className="text-gray-400 truncate">Search...</span>
          </button>

          {/* Right: Quick actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Plan Badge - mobile compact */}
            {planLabel && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full hidden sm:inline-flex ${getTierConfig(user.tier).badge_color}`}>
                {planLabel}
              </span>
            )}
            <NotificationCenter />
            {canCreateAnything && (
            <button
              onClick={() => setOpenNew((v) => !v)}
              className="app-brand-action p-2.5 rounded-lg text-white shadow-md"
              aria-label="Create new"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
            )}
          </div>
        </div>

        {/* Mobile "New" dropdown */}
        {openNew && (
          <div className="app-popover absolute right-4 top-14 mt-1 w-56 rounded-xl shadow-xl py-2 z-50">
            {canCreateQuote && (<button onClick={() => goNew('quote')} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm flex items-center gap-3">
              <div className="app-brand-icon-surface w-8 h-8 rounded-lg flex items-center justify-center">
                <PlusIcon className="h-4 w-4" />
              </div>
              <span className="font-medium">New Quote</span>
            </button>)}
            {canCreateCustomer && (<button onClick={() => goNew('customer')} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                <UserCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <span className="font-medium">New Customer</span>
            </button>)}
          </div>
        )}
      </div>
      )}

      {isDesktop && (
      /* Desktop Header — brand intentionally omitted; the Sidebar shows it on
         desktop, so repeating it here was a duplicate. Mobile keeps brand in
         the topbar because no sidebar is visible on mobile. */
      <div className="app-topbar app-topbar--desktop flex items-center justify-between h-16 px-6 safe-area-top">
        {/* Center Zone: Search Bar */}
        <div className="flex-1 max-w-xl mr-8">
          <button
            type="button"
            onClick={handleSearchClick}
            aria-label={`Search pages, settings and actions (${commandShortcutModifier}+K)`}
            className="app-topbar__search w-full flex items-center gap-3 pl-4 pr-3 py-2.5 border rounded-xl text-sm transition-all shadow-sm"
          >
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            <span className="flex-1 text-gray-400 text-left">Search pages, settings &amp; actions...</span>
            {/* Keyboard shortcut hint */}
            <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 rounded font-medium border border-gray-200 dark:border-slate-600">{commandShortcutModifier}</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 rounded font-medium border border-gray-200 dark:border-slate-600">K</kbd>
            </div>
          </button>
        </div>

        {/* Right Zone: Actions */}
        <div className="flex items-center gap-6 flex-shrink-0">
          {/* Plan Badge */}
          {planLabel && (
            <div className="flex items-center px-3 py-1.5 rounded-full bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
              {/* The pill is gray-100 in light mode and only turns slate-800 under
                  `dark:`, so an unconditional text-white put white on #f3f4f6 —
                  about 1.05:1, i.e. the trial countdown was invisible on every
                  authenticated page for anyone using light mode. The background is
                  opaque, so this is the rendered ratio, not a compositing artifact.
                  The mobile badge at :149 pairs its own colours via getTierConfig
                  and is deliberately left alone. */}
              <span className="text-xs font-semibold text-gray-700 dark:text-white">
                {planLabel}
              </span>
            </div>
          )}

          {/* Notifications */}
          <div className="relative">
            <NotificationCenter />
          </div>

          {/* New Dropdown */}
          {canCreateAnything && (
          <div className="relative" ref={newMenuRef}>
            <button
              onClick={() => { setOpenNew((v) => !v); setUserMenuOpen(false); }}
              className="app-brand-action flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-all"
            >
              <PlusIcon className="h-4 w-4" />
              <span>New</span>
            </button>
            {openNew && (
              <div className="app-popover absolute right-0 mt-2 w-56 rounded-xl shadow-lg py-2 z-50">
                {canCreateQuote && (<button onClick={() => goNew('quote')} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-3">
                  <div className="app-brand-icon-surface w-8 h-8 rounded-lg flex items-center justify-center">
                    <PlusIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium">New Quote</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Create a new quote</div>
                  </div>
                </button>)}
                {canCreateCustomer && (<button onClick={() => goNew('customer')} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                    <UserCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="font-medium">New Customer</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Add a new customer</div>
                  </div>
                </button>)}
                {canCreateQuote && (
                <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-slate-600 mt-2">
                  Jobs are created automatically from accepted quotes
                </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => { setUserMenuOpen((v) => !v); setOpenNew(false); }}
              className="flex items-center gap-2 p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-all"
            >
              <div className="app-brand-avatar w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shadow-sm">
                {(user?.full_name || 'U').charAt(0).toUpperCase()}
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* User Dropdown */}
            {userMenuOpen && (
              <div className="app-popover absolute right-0 mt-2 w-56 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{user?.email}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setUserMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <UserCircleIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Profile Settings
                  </button>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setUserMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <svg className="h-4 w-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Preferences
                  </button>
                </div>
                <div className="border-t border-gray-200 dark:border-slate-600 py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default Topbar;


